import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { getDefaultPanelPath } from "@/lib/authz";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  CheckCircle2,
  Store,
  Receipt,
  Truck,
  ArrowRight,
  ExternalLink,
  Loader2,
  Sparkles,
} from "lucide-react";

const STEPS = [
  { id: "welcome", label: "Hoş Geldiniz" },
  { id: "integrations", label: "Entegrasyonlar" },
  { id: "done", label: "Hazır" },
];

const INTEGRATION_GROUPS = [
  {
    key: "marketplace",
    label: "Pazar Yeri",
    icon: Store,
    color: "text-orange-500",
    bg: "bg-orange-50 dark:bg-orange-950/30",
    items: [
      { key: "trendyol", name: "Trendyol", logo: "/integrations/trendyol.svg" },
      { key: "hepsiburada", name: "Hepsiburada", logo: "/integrations/hepsiburada.svg" },
      { key: "n11", name: "N11", logo: "/integrations/n11.svg" },
    ],
  },
  {
    key: "accounting",
    label: "Muhasebe",
    icon: Receipt,
    color: "text-blue-500",
    bg: "bg-blue-50 dark:bg-blue-950/30",
    items: [
      { key: "parasut", name: "Paraşüt", logo: "/integrations/parasut.png" },
      { key: "bizimhesap", name: "BizimHesap", logo: "/integrations/bizimhesap.svg" },
    ],
  },
  {
    key: "cargo",
    label: "Kargo",
    icon: Truck,
    color: "text-green-500",
    bg: "bg-green-50 dark:bg-green-950/30",
    items: [
      { key: "yurtici", name: "Yurtiçi Kargo", logo: "/integrations/yurtici.svg" },
      { key: "hepsijet", name: "HepsiJet", logo: "/integrations/hepsijet.svg" },
    ],
  },
];

export default function OnboardingPage() {
  const [step, setStep] = useState(0);
  const [completing, setCompleting] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await api.post("/onboarding/complete");
    } catch {
      // non-critical, continue
    }
    navigate(getDefaultPanelPath(user));
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Top progress bar */}
      <div className="h-1 bg-muted w-full">
        <div
          className="h-1 bg-primary transition-all duration-500"
          style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
        />
      </div>

      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-sm">O</span>
          </div>
          <span className="font-semibold text-sm">ObiHub</span>
        </div>
        <div className="flex items-center gap-6">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center gap-2">
              <div
                className={cn(
                  "w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-colors",
                  i < step
                    ? "bg-primary text-primary-foreground"
                    : i === step
                    ? "bg-primary/20 text-primary border border-primary"
                    : "bg-muted text-muted-foreground"
                )}
              >
                {i < step ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
              </div>
              <span
                className={cn(
                  "text-xs hidden sm:block",
                  i === step ? "font-medium text-foreground" : "text-muted-foreground"
                )}
              >
                {s.label}
              </span>
            </div>
          ))}
        </div>
        <button
          onClick={handleComplete}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          Atla
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-lg">
          {step === 0 && <StepWelcome user={user} onNext={() => setStep(1)} />}
          {step === 1 && <StepIntegrations onNext={() => setStep(2)} onBack={() => setStep(0)} />}
          {step === 2 && <StepDone onComplete={handleComplete} completing={completing} />}
        </div>
      </div>
    </div>
  );
}

function StepWelcome({ user, onNext }) {
  return (
    <div className="text-center space-y-8">
      <div className="space-y-3">
        <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto">
          <Sparkles className="h-8 w-8 text-primary" />
        </div>
        <h1 className="text-2xl font-bold text-foreground">
          Hoş geldiniz{user?.full_name ? `, ${user.full_name.split(" ")[0]}` : ""}!
        </h1>
        <p className="text-muted-foreground text-sm max-w-sm mx-auto leading-relaxed">
          ObiHub ile tüm satış kanallarınızı, siparişlerinizi ve faturalarınızı tek ekrandan yönetebilirsiniz.
          Başlamak için birkaç adım var.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4 text-center">
        {[
          { icon: Store, label: "Pazar yerlerinizi bağlayın", color: "text-orange-500", bg: "bg-orange-50 dark:bg-orange-950/30" },
          { icon: Receipt, label: "Faturalarınızı otomatikleştirin", color: "text-blue-500", bg: "bg-blue-50 dark:bg-blue-950/30" },
          { icon: Truck, label: "Kargolarınızı takip edin", color: "text-green-500", bg: "bg-green-50 dark:bg-green-950/30" },
        ].map(({ icon: Icon, label, color, bg }) => (
          <div key={label} className="space-y-2">
            <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center mx-auto", bg)}>
              <Icon className={cn("h-5 w-5", color)} />
            </div>
            <p className="text-xs text-muted-foreground leading-tight">{label}</p>
          </div>
        ))}
      </div>

      <Button onClick={onNext} className="w-full" size="lg">
        Başlayalım <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </div>
  );
}

function StepIntegrations({ onNext, onBack }) {
  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-xl font-bold">Entegrasyonlarınızı seçin</h2>
        <p className="text-sm text-muted-foreground">
          Sonradan da bağlayabilirsiniz. Bu adımı atlayabilirsiniz.
        </p>
      </div>

      <div className="space-y-4">
        {INTEGRATION_GROUPS.map(({ key, label, icon: Icon, color, bg, items }) => (
          <div key={key} className="border rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <div className={cn("w-7 h-7 rounded-lg flex items-center justify-center", bg)}>
                <Icon className={cn("h-4 w-4", color)} />
              </div>
              <span className="text-sm font-medium">{label}</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {items.map((item) => (
                <a
                  key={item.key}
                  href={`/panel/entegrasyonlar`}
                  className="flex flex-col items-center gap-1.5 p-2.5 rounded-lg border border-transparent hover:border-border hover:bg-muted/50 transition-colors cursor-pointer group"
                >
                  <div className="w-8 h-8 rounded-lg bg-background border flex items-center justify-center overflow-hidden">
                    <img
                      src={item.logo}
                      alt={item.name}
                      className="w-6 h-6 object-contain"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  </div>
                  <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors text-center leading-tight">
                    {item.name}
                  </span>
                  <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="flex-1">
          Geri
        </Button>
        <Button onClick={onNext} className="flex-1">
          Devam Et <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function StepDone({ onComplete, completing }) {
  return (
    <div className="text-center space-y-8">
      <div className="space-y-3">
        <div className="w-16 h-16 bg-green-100 dark:bg-green-950/50 rounded-2xl flex items-center justify-center mx-auto">
          <CheckCircle2 className="h-9 w-9 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold">Her şey hazır!</h2>
        <p className="text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">
          ObiHub kullanmaya başlayabilirsiniz. Entegrasyonlarınızı <strong>Ayarlar → Entegrasyonlar</strong> menüsünden her zaman ekleyip yönetebilirsiniz.
        </p>
      </div>

      <div className="bg-muted/50 rounded-xl p-4 space-y-2 text-left">
        {[
          "Siparişleriniz otomatik çekilecek",
          "Faturalar muhasebe programınıza iletilecek",
          "Kargo etiketleri tek tıkla oluşturulacak",
        ].map((item) => (
          <div key={item} className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
            <span className="text-sm text-muted-foreground">{item}</span>
          </div>
        ))}
      </div>

      <Button onClick={onComplete} className="w-full" size="lg" disabled={completing}>
        {completing ? (
          <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Yükleniyor...</>
        ) : (
          <>Panele Git <ArrowRight className="ml-2 h-4 w-4" /></>
        )}
      </Button>
    </div>
  );
}
